import * as Dialog from "@radix-ui/react-dialog";
import { IoClose } from "react-icons/io5";
import { PiDotsThreeVerticalDuotone } from "react-icons/pi";
import "./Drawer.scss";
import { useResizableDrawerWidth } from "./useResizableDrawerWidth";

/**
 * Right-docked, resizable drawer built on Radix `Dialog`.
 *
 * Renders a dimmed overlay, a header with a title and close button, a
 * scrollable body for arbitrary `children`, an optional fixed `footer` below
 * the body, and a vertical drag handle on the left edge that users can drag to
 * resize the drawer width. Optional `bodyClassName` is merged onto that
 * scrollable body (with `drawer__body`) so callers can scope layout or theme
 * styles without an extra wrapper element. A
 * screen-reader description is always present (required by Radix) and is
 * controllable via the `description` prop.
 *
 * @param {object} props
 * @param {boolean} props.open Whether the drawer is open.
 * @param {(open: boolean) => void} props.onOpenChange Radix open-state callback.
 * @param {string} props.title Visible header title (`Dialog.Title`).
 * @param {React.ReactNode} props.description Accessible description (Dialog.Description), visually hidden.
 * @param {React.ReactNode} props.children Drawer body content.
 * @param {string} [props.bodyClassName] Optional class(es) merged onto the scrollable body element (always includes `drawer__body`).
 * @param {string} [props.contentClassName] Optional class(es) merged onto the drawer panel (always includes `drawer__content`).
 * @param {React.ReactNode} [props.footer] Optional footer rendered below the scrollable body (does not scroll with `children`).
 * @returns {import('react').JSX.Element}
 */
const Drawer = ({
    open,
    onOpenChange,
    title,
    description,
    children,
    bodyClassName,
    contentClassName,
    footer,
}) => {
    const { widthPx, onResizePointerDown } = useResizableDrawerWidth(open);

    const bodyClasses = ["drawer__body", bodyClassName]
        .filter(Boolean)
        .join(" ");
    const contentClasses = ["drawer__content", contentClassName]
        .filter(Boolean)
        .join(" ");

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="drawer__overlay" />
                <Dialog.Content
                    className={contentClasses}
                    style={{ width: `${widthPx}px` }}
                >
                    <Dialog.Description className="drawer__sr-only">
                        {description}
                    </Dialog.Description>
                    <div className="drawer__header">
                        <div className="drawer__header-lead">
                            <Dialog.Title className="drawer__title">
                                {title}
                            </Dialog.Title>
                        </div>
                        <Dialog.Close asChild>
                            <button
                                type="button"
                                className="drawer__close"
                                aria-label="Close panel"
                            >
                                <IoClose aria-hidden size={22} />
                            </button>
                        </Dialog.Close>
                    </div>
                    <div className={bodyClasses}>{children}</div>
                    {footer ? (
                        <div className="drawer__footer">{footer}</div>
                    ) : null}
                    <button
                        type="button"
                        className="drawer__resize-handle"
                        onPointerDown={onResizePointerDown}
                        aria-label="Resize panel"
                        title="Drag to change panel width"
                    >
                        <PiDotsThreeVerticalDuotone aria-hidden size={22} />
                    </button>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Drawer;
